import React, { useState, useContext, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft,
  faCheckCircle,
  faCircle,
  faMapMarkerAlt,
  faPhone,
  faMessage,
  faClock,
  faCalendarAlt,
  faUsers,
  faEye,
  faEdit,
  faTrash,
  faCar,
  faUserTie,
  faExternalLinkAlt,
  faDirections,
  faCopy,
  faShare,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { collection, doc, getDoc, getDocs, query } from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import TourContext from '../../context/TourContext';
import UserContext from '../../context/UserContext';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import TopBar from '../TopBar';
import styles from './ScheduleCardDetails.module.css';

const ScheduleCardDetails = () => {
  const { eventId, dateId, scheduleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedEvent, getSelectedDate, events } = useContext(TourContext);
  const { userData } = useContext(UserContext);
  
  // Determine event type from URL
  const eventType = location.pathname.includes('/tour/') ? 'tour' : 'show';
  
  const [schedule, setSchedule] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [assignedContacts, setAssignedContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedStates, setCopiedStates] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadScheduleDetails = async () => {
      try {
        setLoading(true);
        const db = getFirestoreInstance();
        const collectionName = eventType === 'tour' ? 'tours' : 'shows';
        
        // Use scheduleId from URL params as the document ID
        const scheduleRef = doc(db, collectionName, eventId, 'eventDates', dateId, 'schedule', scheduleId);
        const scheduleDoc = await getDoc(scheduleRef);
        
        if (!scheduleDoc.exists()) {
          console.error('Schedule not found with document ID:', scheduleId);
          setLoading(false);
          return;
        }
        
        const scheduleData = {
          docId: scheduleDoc.id, // Store the document ID as docId
          ...scheduleDoc.data()  // Preserve original id field
        };
        
        setSchedule(scheduleData);
        
        // Fetch contacts from the contacts subcollection
        const contactsQuery = query(collection(db, collectionName, eventId, 'eventDates', dateId, 'contacts'));
        const contactsSnapshot = await getDocs(contactsQuery);
        
        const contactsList = contactsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setContacts(contactsList);
        
        // Get assigned contacts for this schedule
        const assignedList = scheduleData.assignedContacts ? 
          contactsList.filter(contact => scheduleData.assignedContacts.includes(contact.id)) : [];
        
        setAssignedContacts(assignedList);
      } catch (error) {
        console.error('Error loading schedule details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (eventId && dateId && scheduleId) {
      loadScheduleDetails();
    }
  }, [eventId, dateId, scheduleId, eventType]);

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Not specified';
    
    try {
      let date;
      
      // Handle different timestamp formats
      if (timestamp?.toDate) {
        // It's a Firestore timestamp
        date = timestamp.toDate();
      } else if (timestamp?.seconds) {
        // It's a serialized Firestore timestamp
        date = new Date(timestamp.seconds * 1000);
      } else if (typeof timestamp === 'string') {
        // It's a string date
        date = new Date(timestamp);
      } else if (timestamp instanceof Date) {
        // It's already a Date object
        date = timestamp;
      } else {
        // Unknown format
        console.warn('Unknown timestamp format:', timestamp);
        return 'Unknown date format';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date value:', timestamp);
        return 'Invalid date';
      }
      
      // Format the date
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'Date error';
    }
  };

  const isSameTimestamp = (timestamp1, timestamp2) => {
    if (!timestamp1 || !timestamp2) return false;
    
    try {
      // Convert both to milliseconds for comparison
      let time1, time2;
      
      if (timestamp1.seconds) {
        time1 = timestamp1.seconds * 1000;
      } else if (timestamp1.toDate) {
        time1 = timestamp1.toDate().getTime();
      } else if (typeof timestamp1 === 'string') {
        time1 = new Date(timestamp1).getTime();
      } else if (timestamp1 instanceof Date) {
        time1 = timestamp1.getTime();
      }
      
      if (timestamp2.seconds) {
        time2 = timestamp2.seconds * 1000;
      } else if (timestamp2.toDate) {
        time2 = timestamp2.toDate().getTime();
      } else if (typeof timestamp2 === 'string') {
        time2 = new Date(timestamp2).getTime();
      } else if (timestamp2 instanceof Date) {
        time2 = timestamp2.getTime();
      }
      
      return time1 === time2;
    } catch (error) {
      console.error('Error comparing timestamps:', error);
      return false;
    }
  };

  const getStatusStyles = (isConfirmed) => {
    return {
      confirmed: {
        backgroundColor: 'var(--confirmed-bg)',
        color: 'var(--confirmed-color)'
      },
      unconfirmed: {
        backgroundColor: 'var(--unconfirmed-bg)',
        color: 'var(--unconfirmed-color)'
      }
    }[isConfirmed ? 'confirmed' : 'unconfirmed'];
  };

  const handleBack = () => {
    navigate(`/${eventType}/${eventId}/${dateId}`);
  };

  const handleEdit = () => {
    // Navigate back with edit state, using scheduleId which is the document ID
    navigate(`/${eventType}/${eventId}/${dateId}`, { 
      state: { editScheduleId: scheduleId } 
    });
  };

  const handleCall = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleMessage = (phoneNumber) => {
    window.location.href = `sms:${phoneNumber}`;
  };

  const handleOpenInMaps = (location) => {
    if (location?.googleMapsUri) {
      window.open(location.googleMapsUri, '_blank');
    } else if (location?.formattedAddress) {
      const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(location.formattedAddress)}`;
      window.open(mapsUrl, '_blank');
    }
  };

  const handleGetDirections = (fromLocation, toLocation) => {
    if (fromLocation && toLocation) {
      const directionsUrl = `https://maps.google.com/maps/dir/${encodeURIComponent(fromLocation.formattedAddress || fromLocation.displayName)}/${encodeURIComponent(toLocation.formattedAddress || toLocation.displayName)}`;
      window.open(directionsUrl, '_blank');
    }
  };

  const handleCopyAddress = (location, locationId) => {
    const addressText = `${location.displayName}\n${location.formattedAddress || location.shortFormattedAddress || ''}`;
    
    navigator.clipboard.writeText(addressText).then(() => {
      // Set copied state for this specific location
      setCopiedStates(prev => ({ ...prev, [locationId]: true }));
      
      // Clear the copied state after 1 second
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [locationId]: false }));
      }, 1000);
    }).catch(err => {
      console.error('Failed to copy address:', err);
    });
  };

  const handleEventSelect = (event) => {
    // Handle event selection if needed
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: schedule.title,
        text: `${schedule.title} - ${formatTime(schedule.startTime)} to ${formatTime(schedule.endTime)}`,
        url: window.location.href
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading schedule details...</p>
      </div>
    );
  }

  if (!schedule) {
    return (
      <div className={styles.error}>
        <h2>Schedule not found</h2>
        <button onClick={handleBack} className={styles.backButton}>
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Schedule
        </button>
      </div>
    );
  }

  const hasDateInfo = schedule.startDate && schedule.endDate;
  const isSameDay = hasDateInfo && 
    new Date(schedule.startDate).toDateString() === new Date(schedule.endDate).toDateString();

  const canEdit = userData?.userId === schedule.creatorId || userData?.userType === 'Manager';

  // Get event name based on event type
  const eventName = eventType === 'show' ? 
    (selectedEvent?.showName || selectedEvent?.tourName) : 
    selectedEvent?.tourName;

  return (
    <div className={styles.layout}>
      <TopBar />
      <div className={styles.mainLayout}>
        {!isMobile && <LeftMenu selectedEvent={selectedEvent} />}
        {!isMobile && (
          <RightMenu
            selectedEvent={selectedEvent}
            events={events}
            onEventSelect={handleEventSelect}
          />
        )}
        
        <div className={styles.container}>
          {/* Header */}
          <div className={styles.header}>
            <button onClick={handleBack} className={styles.backButton}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h1 className={styles.title}>Schedule Details</h1>
            <div className={styles.headerActions}>
              <button onClick={handleShare} className={styles.actionButton}>
                <FontAwesomeIcon icon={faShare} />
              </button>
              {canEdit && (
                <button onClick={handleEdit} className={styles.actionButton}>
                  <FontAwesomeIcon icon={faEdit} />
                </button>
              )}
            </div>
          </div>

      {/* Main Content */}
      <div className={styles.content}>
        
        {/* Schedule Overview */}
        <div className={styles.card}>
        <div className={styles.scheduleHeader}>
  <div className={styles.scheduleTitle}>
    <h2>{schedule.title}</h2>
    <div 
      className={styles.statusBadge}
      style={getStatusStyles(schedule.isConfirmed)}
    >
      <FontAwesomeIcon 
        icon={schedule.isConfirmed ? faCheckCircle : faCircle}
        className={schedule.isConfirmed ? styles.confirmed : styles.unconfirmed}
      />
      <span>{schedule.isConfirmed ? 'Confirmed' : 'Unconfirmed'}</span>
    </div>
  </div>
</div>

          {/* Date and Time */}
          <div className={styles.dateTimeSection}>
            <div className={styles.dateTime}>
              <FontAwesomeIcon icon={faCalendarAlt} className={styles.icon} />
              <div>
                <div className={styles.date}>
                  {hasDateInfo ? (
                    <>
                      {formatDate(schedule.startDate)}
                      {!isSameDay && (
                        <span className={styles.dateRange}>
                          {' '} - {formatDate(schedule.endDate)}
                        </span>
                      )}
                    </>
                  ) : (
                    'Date not specified'
                  )}
                </div>
                <div className={styles.time}>
                  <FontAwesomeIcon icon={faClock} className={styles.timeIcon} />
                  {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Locations */}
        {(schedule.fromLocation || schedule.toLocation) && (
          <div className={styles.card}>
            <h3 className={styles.sectionTitle}>
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              Locations
            </h3>
            
            {schedule.fromLocation && schedule.toLocation && (
              <div className={styles.directionsHeader}>
                <button 
                  onClick={() => handleGetDirections(schedule.fromLocation, schedule.toLocation)}
                  className={styles.directionsButton}
                >
                  <FontAwesomeIcon icon={faDirections} />
                  Get Directions
                </button>
              </div>
            )}

            <div className={styles.locationsContainer}>
              {schedule.fromLocation && (
                <div className={styles.locationCard}>
                  <div className={styles.locationHeader}>
                    <div className={styles.locationBadge}>
                      <span className={styles.fromBadge}>FROM</span>
                    </div>
                    <div className={styles.locationActions}>
                      <div className={styles.copyButtonContainer}>
                        <button 
                          onClick={() => handleCopyAddress(schedule.fromLocation, 'from')}
                          className={styles.locationAction}
                          title="Copy address"
                        >
                          <FontAwesomeIcon icon={copiedStates.from ? faCheck : faCopy} />
                        </button>
                        {copiedStates.from && (
                          <div className={styles.copiedTooltip}>Copied!</div>
                        )}
                      </div>
                      <button 
                        onClick={() => handleOpenInMaps(schedule.fromLocation)}
                        className={styles.locationAction}
                        title="Open in Maps"
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </button>
                    </div>
                  </div>
                  
                  <div className={styles.locationInfo}>
                    <h4 className={styles.locationName}>{schedule.fromLocation.displayName}</h4>
                    <p className={styles.locationAddress}>{schedule.fromLocation.formattedAddress}</p>
                  </div>

                  {/* Embedded Map */}
                  <div className={styles.mapContainer}>
                    <iframe
                      src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_MAPS_EMBED_KEY}&q=${encodeURIComponent(schedule.fromLocation.formattedAddress)}&zoom=15`}
                      className={styles.map}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={`Map of ${schedule.fromLocation.displayName}`}
                    />
                  </div>
                </div>
              )}

              {schedule.toLocation && (
                <div className={styles.locationCard}>
                  <div className={styles.locationHeader}>
                    <div className={styles.locationBadge}>
                      <span className={styles.toBadge}>TO</span>
                    </div>
                    <div className={styles.locationActions}>
                      <div className={styles.copyButtonContainer}>
                        <button 
                          onClick={() => handleCopyAddress(schedule.toLocation, 'to')}
                          className={styles.locationAction}
                          title="Copy address"
                        >
                          <FontAwesomeIcon icon={copiedStates.to ? faCheck : faCopy} />
                        </button>
                        {copiedStates.to && (
                          <div className={styles.copiedTooltip}>Copied!</div>
                        )}
                      </div>
                      <button 
                        onClick={() => handleOpenInMaps(schedule.toLocation)}
                        className={styles.locationAction}
                        title="Open in Maps"
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </button>
                    </div>
                  </div>
                  
                  <div className={styles.locationInfo}>
                    <h4 className={styles.locationName}>{schedule.toLocation.displayName}</h4>
                    <p className={styles.locationAddress}>{schedule.toLocation.formattedAddress}</p>
                  </div>

                  {/* Embedded Map */}
                  <div className={styles.mapContainer}>
                    <iframe
                      src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_MAPS_EMBED_KEY}&q=${encodeURIComponent(schedule.toLocation.formattedAddress)}&zoom=15`}
                      className={styles.map}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={`Map of ${schedule.toLocation.displayName}`}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Assigned Contacts */}
        {assignedContacts.length > 0 && (
          <div className={styles.card}>
            <h3 className={styles.sectionTitle}>
              <FontAwesomeIcon icon={faUsers} />
              Assigned Contacts ({assignedContacts.length})
            </h3>
            
            <div className={styles.contactsContainer}>
              {assignedContacts.map((contact) => (
                <div key={contact.id} className={styles.contactCard}>
                  <div className={styles.contactHeader}>
                    <div className={styles.contactInfo}>
                      <div className={styles.contactName}>{contact.name}</div>
                      <div className={styles.contactRole}>
                        <FontAwesomeIcon 
                          icon={contact.role === 'driver' ? faCar : faUserTie} 
                          className={styles.roleIcon}
                        />
                        {contact.role.charAt(0).toUpperCase() + contact.role.slice(1)}
                      </div>
                    </div>
                    
                    <div className={styles.contactActions}>
                      <button 
                        onClick={() => handleCall(contact.phone)}
                        className={styles.contactAction}
                        title="Call"
                      >
                        <FontAwesomeIcon icon={faPhone} />
                      </button>
                      <button 
                        onClick={() => handleMessage(contact.phone)}
                        className={styles.contactAction}
                        title="Message"
                      >
                        <FontAwesomeIcon icon={faMessage} />
                      </button>
                    </div>
                  </div>
                  
                  <div className={styles.contactDetails}>
                    <div className={styles.phoneNumber}>{contact.phone}</div>
                    
                    {contact.role === 'driver' && contact.vehicleDetails && (
                      <div className={styles.vehicleInfo}>
                        <FontAwesomeIcon icon={faCar} className={styles.vehicleIcon} />
                        <span>
                          {contact.vehicleDetails.name} • {contact.vehicleDetails.color} • {contact.vehicleDetails.number}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {schedule.notes && (
          <div className={styles.card}>
            <h3 className={styles.sectionTitle}>Notes</h3>
            <div className={styles.notesContent}>
              <p>{schedule.notes}</p>
            </div>
          </div>
        )}

        {/* Visibility & Metadata */}
        {(userData.userId === schedule.creatorId || userData.userType === 'Manager') && (
  <div className={styles.card}>
    <h3 className={styles.sectionTitle}>
      <FontAwesomeIcon icon={faEye} />
      Details
    </h3>
    
    <div className={styles.metadataContainer}>
      <div className={styles.metadataItem}>
        <span className={styles.metadataLabel}>Visibility:</span>
        <span className={styles.metadataValue}>
          {schedule.visibilityType ? 
            schedule.visibilityType.charAt(0).toUpperCase() + schedule.visibilityType.slice(1) : 
            'All Members'
          }
        </span>
      </div>
      
      <div className={styles.metadataItem}>
        <span className={styles.metadataLabel}>Created:</span>
        <span className={styles.metadataValue}>
          {formatDate(schedule.createdAt)}
        </span>
      </div>
      
      {schedule.updatedAt && !isSameTimestamp(schedule.updatedAt, schedule.createdAt) && (
        <div className={styles.metadataItem}>
          <span className={styles.metadataLabel}>Last Updated:</span>
          <span className={styles.metadataValue}>
            {formatDate(schedule.updatedAt)}
          </span>
        </div>
      )}
    </div>
  </div>
)}

          
      </div>
    </div>
    </div>
    </div>
  );
};

export default ScheduleCardDetails;